import React, {useState} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- THEME / COLOR CONSTANTS ---
const COLOR_PRIMARY = '#ff5722';
const COLOR_PRIMARY_DARK = '#e64a19';
const COLOR_SECONDARY = '#a0522d';
const COLOR_ACCENT_BG = '#fff0e6';
const COLOR_TEXT = '#333333';
const COLOR_TEXT_LIGHT = '#666666';
const COLOR_BG_LIGHT = '#f8f8f8';
const COLOR_WHITE = '#ffffff';
const COLOR_BORDER_LIGHT = '#e0e0e0';
const COLOR_SUCCESS = '#4CAF50';
const COLOR_SUCCESS_LIGHT = '#8BC34A';

// --- GLOBAL STYLES ---
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    color: ${COLOR_TEXT};
    background-color: ${COLOR_WHITE};
    line-height: 1.6;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    font-weight: 700;
    line-height: 1.2;
  }

  * {
    box-sizing: border-box;
  }
`;

// --- LAYOUT COMPONENTS ---
const SectionContainer = styled.div`
  padding: 80px 5%;
  background-color: ${(props) => (props.bgLight ? COLOR_BG_LIGHT : COLOR_WHITE)};
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const SectionHeader = styled.div`
  background: ${(props) => props.gradient || `linear-gradient(to right, #ff8c00, ${COLOR_PRIMARY})`};
  padding: 25px 40px;
  margin-bottom: 50px;
  border-radius: 8px;
  display: inline-block;
  max-width: fit-content;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 20px 30px;
    margin-bottom: 40px;
    width: 100%;
    max-width: none;
  }
`;

const MainTitle = styled.h2`
  font-size: ${(props) => props.size === 'large' ? '48px' : '40px'};
  color: ${(props) => props.color || COLOR_PRIMARY_DARK};
  margin: 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: ${(props) => props.size === 'large' ? '36px' : '28px'};
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.whiteText ? COLOR_WHITE : COLOR_TEXT_LIGHT)};
  max-width: 800px;
  margin: 0 auto 50px;
  line-height: 1.6;
  text-align: center;
`;

// --- BUTTON COMPONENTS ---
const BaseButton = styled.button`
  background-color: ${COLOR_PRIMARY};
  color: ${COLOR_WHITE};
  border: none;
  padding: 15px 35px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 20px;
  display: inline-block;
  text-decoration: none;

  &:hover {
    background-color: ${COLOR_PRIMARY_DARK};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CallToActionButton = styled(BaseButton)``;

const LinkButton = styled(BaseButton).attrs({ as: 'a' })``;

const GetStartedButton = styled(BaseButton)`
  padding: 10px 20px;
  font-size: 14px;
  margin-top: 15px;
`;

// --- TEXT COMPONENTS ---
const AccentText = styled.span`
  color: ${COLOR_PRIMARY};
  font-weight: 900;
`;

const DescriptionParagraph = styled.p`
  font-size: 16px;
  color: ${COLOR_TEXT_LIGHT};
  margin-bottom: 20px;
  line-height: 1.7;
`;

// --- CARD COMPONENTS ---
const Card = styled.div`
  background-color: ${COLOR_WHITE};
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-align: left;
  border-top: 3px solid ${(props) => props.accentColor || COLOR_PRIMARY};

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const IconPlaceholder = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${(props) => props.color || COLOR_PRIMARY};
  border-radius: 50%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_WHITE};
  font-size: 24px;
  font-weight: bold;
`;

const CardTitle = styled.h3`
  font-size: 22px;
  margin-bottom: 15px;
  color: ${(props) => props.color || COLOR_PRIMARY};
`;

const CardDescription = styled.p`
  font-size: 16px;
  color: ${COLOR_TEXT_LIGHT};
  line-height: 1.6;
`;

// --- 1. HERO SECTION COMPONENTS ---
const HeroSection = styled(SectionContainer)`
  background: linear-gradient(135deg, ${COLOR_BG_LIGHT} 0%, #f0f0f0 100%);
  padding-top: 120px;
  padding-bottom: 120px;
  text-align: left;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, ${COLOR_PRIMARY}20 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const HeroContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
  }
`;

const TextColumn = styled.div`
  flex: 1;
  max-width: 50%;

  @media (max-width: 1024px) {
    max-width: 100%;
    margin-bottom: 40px;
  }
`;

const ImageColumn = styled.div`
  flex: 1;
  max-width: 50%;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 1024px) {
    max-width: 100%;
    justify-content: center;
  }
`;

const HeroHeadline = styled.h1`
  font-size: 56px;
  line-height: 1.1;
  margin-bottom: 20px;
  color: ${COLOR_TEXT};

  @media (max-width: 1024px) {
    font-size: 44px;
  }
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const StatisticCard = styled.div`
  background-color: ${COLOR_WHITE};
  padding: 25px 30px;
  border-left: 5px solid ${COLOR_PRIMARY};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  margin-top: 30px;
  display: inline-block;
  text-align: left;
  border-radius: 0 8px 8px 0;
`;

const StatisticNumber = styled.p`
  font-size: 48px;
  font-weight: 900;
  color: ${COLOR_TEXT};
  margin: 0;
  line-height: 1;
`;

const StatisticText = styled.p`
  font-size: 16px;
  color: ${COLOR_TEXT_LIGHT};
  margin: 5px 0 0 0;
`;

const MockupImage = styled.div`
  width: 100%;
  max-width: 550px;
  height: 350px;
  background: linear-gradient(135deg, ${COLOR_PRIMARY} 0%, ${COLOR_PRIMARY}cc 100%);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_WHITE};
  font-weight: bold;
  font-size: 18px;
`;

// --- 2. SERVICES SECTION COMPONENTS ---
const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 50px auto 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(Card)`
  text-align: center;
  padding: 40px 30px;

  ${IconPlaceholder} {
    margin: 0 auto 20px auto;
  }
`;

// --- 3. PROCESS SECTION COMPONENTS ---
const ProcessGrid = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  margin: 50px auto 0;
  position: relative;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ProcessStep = styled.div`
  flex: 1;
  padding: 20px;
  position: relative;
  text-align: center;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 60px;
    right: -15%;
    width: 30%; 
    height: 2px;
    background-color: ${COLOR_SECONDARY};
    z-index: 0;

    @media (max-width: 1024px) {
      content: none;
    }
  }

  @media (max-width: 1024px) {
    text-align: left;
    display: flex;
    align-items: flex-start;
    padding: 0;
  }
`;

const StepContent = styled.div`
  z-index: 1;
  position: relative;
  background: transparent;

  @media (max-width: 1024px) {
    margin-left: 20px;
    flex: 1;
  }
`;

const StepIcon = styled(IconPlaceholder)`
  margin: 0 auto 20px auto;
  font-size: 20px;
  
  @media (max-width: 1024px) {
    margin: 0;
    flex-shrink: 0;
    width: 50px;
    height: 50px;
  }
`;

const StepTitle = styled.h4`
  font-size: 20px;
  margin-bottom: 15px;
  color: ${COLOR_TEXT};
`;

const StepDescription = styled.p`
  font-size: 15px;
  color: ${COLOR_TEXT_LIGHT};
  line-height: 1.6;
`;

// --- 4. WHY CUSTOM SECTION COMPONENTS ---
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 50px auto 0;
  text-align: left;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(Card)`
  border-top: none;
  border-bottom: 3px solid ${COLOR_SECONDARY};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const FeatureIcon = styled(IconPlaceholder)`
  background-color: ${COLOR_SECONDARY};
`;

// --- 5. WHY CHOOSE SECTION COMPONENTS ---
const WhyChooseSection = styled(SectionContainer)`
  padding-top: 60px;
  background-color: ${COLOR_ACCENT_BG};
  text-align: left;
`;

const WhyChooseContent = styled.div`
  display: flex;
  gap: 60px;
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
    gap: 40px;
  }
`;

const TextBlock = styled.div`
  flex: 1;
  max-width: 50%;
  padding: 20px;

  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 0;
  }
`;

const ImageBlock = styled.div`
  flex: 1;
  max-width: 50%;
  min-height: 300px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_WHITE};
  font-weight: bold;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  font-size: 18px;

  &.team-illustration {
    background: linear-gradient(45deg, #a7d9e2 0%, #7db9b3 100%);
  }

  &.dashboard-illustration {
    background: linear-gradient(45deg, #fceabb 0%, #f8b500 100%);
  }

  &.rocket-illustration {
    background: linear-gradient(45deg, #ffa07a 0%, #ff6347 100%);
  }

  @media (max-width: 1024px) {
    max-width: 90%;
    min-height: 250px;
  }
`;

// --- 6. STRATEGY SECTION COMPONENTS ---
const StrategyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  max-width: 1200px;
  margin: 50px auto;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StrategyCard = styled.div`
  display: flex;
  align-items: center;
  text-align: left;
  background-color: ${COLOR_WHITE};
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 30px;
  }
`;

const StrategyText = styled.div`
  flex: 2;
  padding-right: 30px;

  @media (max-width: 768px) {
    padding-right: 0;
    margin-top: 20px;
  }
`;

const StrategyImage = styled(ImageBlock)`
  flex: 1;
  min-height: 200px;
  max-width: 250px;
  border-radius: 8px;

  @media (max-width: 768px) {
    max-width: 100%;
    min-height: 180px;
    order: -1;
  }
`;

// --- 7. WORDPRESS SECTION COMPONENTS ---
const WhyWordPressSection = styled(SectionContainer)`
  background-color: ${COLOR_BG_LIGHT};
  padding-top: 60px;
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 50px auto;
  text-align: left;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(Card)`
  border-top: 3px solid ${COLOR_SUCCESS};
`;

const BenefitTitle = styled(CardTitle)`
  color: ${COLOR_SUCCESS};
`;

const WordPressLogoPlaceholder = styled.div`
  max-width: 200px;
  height: 120px;
  margin: 40px auto;
  border-radius: 8px;
  background: linear-gradient(145deg, #21759b 0%, #1d5f7e 100%);
  border: 1px solid #1a516b;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${COLOR_WHITE};
  font-weight: normal;
`;

const SmallCard = styled.div`
  background-color: ${COLOR_WHITE};
  padding: 25px 30px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  margin: 40px auto 0;
  text-align: left;
  max-width: 800px;
  border-left: 4px solid ${COLOR_SUCCESS};
`;

const CallToActionBox = styled.div`
  background: linear-gradient(135deg, ${COLOR_PRIMARY} 0%, ${COLOR_PRIMARY_DARK} 100%);
  color: ${COLOR_WHITE};
  padding: 40px;
  border-radius: 12px;
  max-width: 900px;
  margin: 60px auto 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  text-align: left;
  box-shadow: 0 10px 30px rgba(255, 87, 34, 0.2);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 30px;
  }
`;

const CallToActionText = styled.p`
  font-size: 20px;
  margin: 0;
  flex: 1;
  padding-right: 30px;
  font-weight: 600;

  @media (max-width: 768px) {
    padding-right: 0;
    margin-bottom: 20px;
    font-size: 18px;
  }
`;

// --- 8. RESPONSIVE SHOWCASE COMPONENTS ---
const ResponsiveSection = styled(SectionContainer)`
  padding-top: 60px;
`;

const DeviceMockups = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 40px;
  margin-top: 60px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const DeviceFrame = styled.div`
  background-color: #222;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLOR_WHITE};
  font-weight: bold;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-10px);
  }
`;

const DesktopMockup = styled(DeviceFrame)`
  width: 450px;
  height: 280px;
  
  .screen {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${COLOR_WHITE};
  }

  @media (max-width: 768px) {
    width: 90%;
    height: 220px;
  }
`;

const TabletMockup = styled(DeviceFrame)`
  width: 280px;
  height: 380px;

  .screen {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: ${COLOR_WHITE};
  }

  @media (max-width: 768px) {
    width: 220px;
    height: 320px;
  }
`;

const PhoneMockup = styled(DeviceFrame)`
  width: 160px;
  height: 320px;

  .screen {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: ${COLOR_WHITE};
  }

  @media (max-width: 768px) {
    width: 130px;
    height: 260px;
  }
`;

// --- 9. FAQ SECTION COMPONENTS ---
const FAQSection = styled(SectionContainer)`
  background-color: ${COLOR_ACCENT_BG};
`;

const FAQItem = styled.div`
  background-color: ${COLOR_WHITE};
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }
`;

const FAQQuestion = styled.div`
  padding: 25px 30px;
  font-size: 18px;
  font-weight: 600;
  color: ${COLOR_TEXT};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafafa;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const FAQAnswer = styled.div`
  padding: 25px 30px;
  font-size: 16px;
  color: ${COLOR_TEXT_LIGHT};
  border-top: 1px solid ${COLOR_BORDER_LIGHT};
  text-align: left;
  line-height: 1.7;
  background-color: ${COLOR_WHITE};
`;

const ToggleIcon = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: ${COLOR_PRIMARY};
  transition: transform 0.3s ease;
  transform: rotate(${(props) => (props.isOpen ? '180deg' : '0deg')});
  margin-left: 15px;
`;

const Footer = styled.div`
  padding: 40px 30px;
  text-align: center;
  background-color: ${COLOR_TEXT};
  color: ${COLOR_WHITE};
  font-size: 14px;
`;

// --- MAIN PAGE COMPONENT ---
const CustomWebDesign = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleButtonClick = (action) => {
    console.log(`${action} button clicked`);
    alert(`Action: ${action} - Console log updated.`);
  };

  const faqs = [
    {
      question: 'What makes SRK Softwares different from other web design companies?',
      answer: 'SRK Softwares focuses on a holistic approach, combining cutting-edge design with robust SEO strategies and client-centric processes to deliver measurable business results, not just pretty websites.',
    },
    {
      question: 'How long does it take to build a custom WordPress website?',
      answer: 'The timeline varies depending on the complexity and features required. A typical custom WordPress website can take anywhere from 4 to 12 weeks from initial consultation to launch. We provide a detailed project schedule after the discovery phase.',
    },
    {
      question: 'Can you help with website maintenance and updates?',
      answer: 'Absolutely! We offer comprehensive maintenance packages to ensure your WordPress website remains secure, up-to-date, and performs optimally. This includes regular backups, security checks, software updates, and content management support.',
    },
    {
      question: 'Is WordPress suitable for large-scale e-commerce websites?',
      answer: 'Yes, WordPress, powered by WooCommerce, is highly capable of handling large-scale e-commerce operations. Its flexibility, vast plugin ecosystem, and scalability make it an excellent choice for businesses of all sizes looking for robust online store solutions.',
    },
    {
      question: 'What is your process for search engine optimization (SEO)?',
      answer: 'Our SEO process is integrated from the initial design phase. We perform keyword research, optimize site structure, ensure technical SEO best practices, and implement content strategies to help your website rank higher on search engines and attract organic traffic.',
    },
  ];

  return (
    <Home>
      <GlobalStyle />
      
      {/* 1. HERO SECTION */}
      <HeroSection>
        <HeroContent>
          <TextColumn>
            <HeroHeadline>
              Turning Visions Into Realities Through <AccentText>Custom Web Design</AccentText>
            </HeroHeadline>
            <DescriptionParagraph>
              As a leading <strong>Custom Web Design Company</strong>, <strong>SRK Softwares</strong> specializes in building bespoke, high-performance websites that align perfectly with your business goals. We don't just design; we strategize for success.
            </DescriptionParagraph>
            <CallToActionButton onClick={() => handleButtonClick('Get Started')}>
              Start Your Project
            </CallToActionButton>
          </TextColumn>
          <ImageColumn>
            <MockupImage>Website Mockup Preview</MockupImage>
          </ImageColumn>
        </HeroContent>
      </HeroSection>

      {/* 2. CUSTOM WEB DESIGN SERVICES SECTION */}
      <SectionContainer bgLight>
        <MainTitle>Custom Web Design Services</MainTitle>
        <Subtitle>
          We offer comprehensive services tailored to meet the unique digital needs of your business, ensuring a powerful and impactful online presence.
        </Subtitle>
        <ServiceGrid>
          <ServiceCard>
            <IconPlaceholder>W</IconPlaceholder>
            <CardTitle>WordPress Experts</CardTitle>
            <CardDescription>
              Specializing in custom WordPress development to give you a powerful, flexible, and easy-to-manage website.
            </CardDescription>
          </ServiceCard>
          <ServiceCard>
            <IconPlaceholder>E</IconPlaceholder>
            <CardTitle>Website Evolution</CardTitle>
            <CardDescription>
              Transforming outdated sites into modern, responsive, and conversion-optimized digital experiences.
            </CardDescription>
          </ServiceCard>
          <ServiceCard>
            <IconPlaceholder>R</IconPlaceholder>
            <CardTitle>Worldwide Reach</CardTitle>
            <CardDescription>
              Designing global-ready websites with multi-language support and international SEO best practices.
            </CardDescription>
          </ServiceCard>
        </ServiceGrid>
      </SectionContainer>

      {/* 3. CUSTOM WORDPRESS WEBSITE DESIGN PROCESS SECTION */}
      <SectionContainer>
        <MainTitle>Custom WordPress Website Design Process</MainTitle>
        <Subtitle>
          Our streamlined four-step process ensures transparency, collaboration, and a final product that exceeds expectations.
        </Subtitle>
        <ProcessGrid>
          <ProcessStep>
            <StepIcon>1</StepIcon>
            <StepContent>
              <StepTitle>Initial Consultation</StepTitle>
              <StepDescription>
                We begin by understanding your vision, goals, and target audience to define the project scope.
              </StepDescription>
              <GetStartedButton onClick={() => handleButtonClick('Process Step 1')}>
                Get Started
              </GetStartedButton>
            </StepContent>
          </ProcessStep>
          <ProcessStep>
            <StepIcon>2</StepIcon>
            <StepContent>
              <StepTitle>Strategy & Design</StepTitle>
              <StepDescription>
                Creating wireframes, mockups, and a detailed plan before a single line of code is written.
              </StepDescription>
              <GetStartedButton onClick={() => handleButtonClick('Process Step 2')}>
                Get Started
              </GetStartedButton>
            </StepContent>
          </ProcessStep>
          <ProcessStep>
            <StepIcon>3</StepIcon>
            <StepContent>
              <StepTitle>Development & Testing</StepTitle>
              <StepDescription>
                Building the site with clean code and rigorously testing on all devices and browsers.
              </StepDescription>
              <GetStartedButton onClick={() => handleButtonClick('Process Step 3')}>
                Get Started
              </GetStartedButton>
            </StepContent>
          </ProcessStep>
          <ProcessStep>
            <StepIcon>4</StepIcon>
            <StepContent>
              <StepTitle>Launch & Support</StepTitle>
              <StepDescription>
                Deploying your site and providing ongoing support and maintenance to ensure peak performance.
              </StepDescription>
              <GetStartedButton onClick={() => handleButtonClick('Process Step 4')}>
                Get Started
              </GetStartedButton>
            </StepContent>
          </ProcessStep>
        </ProcessGrid>
      </SectionContainer>

      {/* 4. WHY CUSTOM WEBSITE SECTION */}
      <SectionContainer bgLight>
        <MainTitle>Why My Business Needs A Custom Website Designer Near Me</MainTitle>
        <Subtitle>
          A custom website from <strong>SRK Softwares</strong> is not an expense; it's an investment in a future-proof, high-converting digital asset.
        </Subtitle>
        <FeatureGrid>
          <FeatureCard>
            <FeatureIcon>ID</FeatureIcon>
            <CardTitle>Distinct Brand Identity</CardTitle>
            <CardDescription>
              Your website will stand out from competitors, clearly articulating your unique value proposition.
            </CardDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>UX</FeatureIcon>
            <CardTitle>Tailored User Experience</CardTitle>
            <CardDescription>
              Designed specifically for your audience, guiding them seamlessly toward conversion.
            </CardDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>C</FeatureIcon>
            <CardTitle>Optimized for Conversions</CardTitle>
            <CardDescription>
              Every element is placed strategically to maximize lead generation and sales.
            </CardDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>R</FeatureIcon>
            <CardTitle>Responsive and Mobile-Friendly</CardTitle>
            <CardDescription>
              Ensuring a perfect display and function on all devices, from desktop to smartphone.
            </CardDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>SEO</FeatureIcon>
            <CardTitle>Search Engine Optimized</CardTitle>
            <CardDescription>
              Built from the ground up with a solid SEO foundation for higher search rankings.
            </CardDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>S</FeatureIcon>
            <CardTitle>Scalability and Future-Proofing</CardTitle>
            <CardDescription>
              Easily accommodates future growth, new features, and changes in technology.
            </CardDescription>
          </FeatureCard>
        </FeatureGrid>
        <CallToActionButton onClick={() => handleButtonClick("Let's Talk")}>
          LET'S TALK
        </CallToActionButton>
      </SectionContainer>

      {/* 5. WHY CHOOSE SECTION */}
      <WhyChooseSection>
        <SectionHeader>
          <MainTitle>Why Choose <AccentText>SRK Softwares</AccentText> As Your WordPress Web Design Company?</MainTitle>
        </SectionHeader>
        <WhyChooseContent>
          <ImageBlock className="team-illustration">
            Team Collaboration Illustration
          </ImageBlock>
          <TextBlock>
            <Subtitle style={{ marginBottom: '20px', textAlign: 'left', maxWidth: 'none' }}>
              We Are More Than Just A WordPress Agency
            </Subtitle>
            <DescriptionParagraph>
              Aside from WordPress website design, the SRK Softwares team excels in creating comprehensive digital marketing strategies, including SEO, pay-per-click, social media, and more. By seamlessly integrating these services, we enhance your online visibility, drive targeted traffic, and maximize the overall agency's WordPress web design price flexibility.
            </DescriptionParagraph>
          </TextBlock>
        </WhyChooseContent>
      </WhyChooseSection>

      {/* 6. CLIENT-CENTRIC STRATEGIES & SALES-FOCUSED SITES */}
      <SectionContainer bgLight>
        <StrategyGrid>
          <StrategyCard>
            <StrategyText>
              <CardTitle>Client-Centric Strategies</CardTitle>
              <CardDescription>
                SRK Softwares develops platforms for specific company objectives, which serve as the foundation for our design process. We aim to help our clients attain online prominence through effective branding, lead identification, and selling things. Our WordPress management is always client-centric, from the initial consultation until the launching of the website.
              </CardDescription>
            </StrategyText>
            <StrategyImage className="dashboard-illustration">
              Dashboard UI Illustration
            </StrategyImage>
          </StrategyCard>
          <StrategyCard>
            <StrategyText>
              <CardTitle>Sales-Focused Sites</CardTitle>
              <CardDescription>
                The most significant benefit of working with our full-service WordPress web design firm is that we understand how vital it is for your website to generate money. We incorporate conversion requirements into the WordPress web design cost. We work with clients to find out what you want your website to do and then integrate these conversion-maximizing techniques to boost your sales on web.
              </CardDescription>
            </StrategyText>
            <StrategyImage className="rocket-illustration">
              Rocket Launch Illustration
            </StrategyImage>
          </StrategyCard>
        </StrategyGrid>
      </SectionContainer>

      {/* 7. WHY WORDPRESS? THE POWER BEHIND YOUR ONLINE PRESENCE */}
      <WhyWordPressSection>
        <SectionHeader gradient={`linear-gradient(to right, ${COLOR_SUCCESS}, ${COLOR_SUCCESS_LIGHT})`}>
          <MainTitle>Why WordPress? The Power Behind Your Online Presence</MainTitle>
        </SectionHeader>
        <Subtitle>
          WordPress is the leading content management system globally, known for its flexibility, powerful features, vast ecosystem, and open-source nature. Choosing WordPress means building on a robust and future-proof platform.
        </Subtitle>

        <BenefitGrid>
          <BenefitCard>
            <BenefitTitle>User-Friendly Interface</BenefitTitle>
            <CardDescription>
              One of the key attractions of WordPress is its intuitive, user-friendly interface. Even without technical expertise, you can easily manage the WordPress dashboard, update content, and manage your website effectively. This ease of use makes it a popular choice for businesses of all sizes.
            </CardDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitTitle>Versatility and Flexibility</BenefitTitle>
            <CardDescription>
              WordPress's versatility is a major selling point. It's not limited to a specific type of website; it can be customized to suit various needs, whether you need a corporate website, an e-commerce store, a portfolio website, a news site, or a blog.
            </CardDescription>
          </BenefitCard>
          <BenefitCard>
            <BenefitTitle>Responsive Design</BenefitTitle>
            <CardDescription>
              In today's mobile-first world, having a responsive website is crucial. WordPress offers a wide selection of responsive themes that ensure your site looks and functions seamlessly on smartphones, tablets, and desktops.
            </CardDescription>
          </BenefitCard>
        </BenefitGrid>

        <SmallCard>
          <BenefitTitle>Robust Plugin Ecosystem</BenefitTitle>
          <CardDescription>
            WordPress boasts a vast ecosystem of plugins, both free and premium. These plugins add functionality to your site without requiring extensive coding, from SEO optimization and security enhancements to e-commerce features and contact forms, expanding your website's capabilities.
          </CardDescription>
        </SmallCard>
        
        <WordPressLogoPlaceholder>
          WordPress
        </WordPressLogoPlaceholder>

        <CallToActionBox>
          <CallToActionText>
            Get Deeper Insights Into The Details And Uncover The REAL SEO With Video Recorded Audit Reports.
          </CallToActionText>
          <LinkButton href="#audit-report" onClick={() => console.log('Audit Report Clicked')}>
            Video Audit Report
          </LinkButton>
        </CallToActionBox>
      </WhyWordPressSection>

      {/* 8. RESPONSIVE WEBSITE SHOWCASE */}
      <ResponsiveSection bgLight>
        <MainTitle style={{ color: COLOR_TEXT }}>Responsive Website</MainTitle>
        <Subtitle>
          A responsive website automatically adjusts and optimizes its layout and content to fit the screen size and resolution of any device it's accessed on. Whether your visitors are using smartphones, tablets, laptops, or desktop computers, a responsive website ensures that they have an optimal viewing experience.
        </Subtitle>
        <DeviceMockups>
          <DesktopMockup>
            <div className="screen">Desktop View</div>
          </DesktopMockup>
          <TabletMockup>
            <div className="screen">Tablet View</div>
          </TabletMockup>
          <PhoneMockup>
            <div className="screen">Mobile View</div>
          </PhoneMockup>
        </DeviceMockups>
        <CallToActionBox style={{ background: `linear-gradient(135deg, #2196F3 0%, #1976D2 100%)`, marginTop: '80px' }}>
          <CallToActionText>
            To Enhance The User Experience With Responsive And Lead-Focused Website Designs.
          </CallToActionText>
          <LinkButton href="#contact" onClick={() => console.log('Enhance User Experience Clicked')} style={{ backgroundColor: '#1565C0' }}>
            Get Started Now
          </LinkButton>
        </CallToActionBox>
      </ResponsiveSection>

      {/* 9. FREQUENTLY ASKED QUESTIONS (FAQ) */}
      <FAQSection>
        <MainTitle style={{ color: COLOR_TEXT, marginBottom: '50px' }}>Frequently Asked Questions</MainTitle>
        {faqs.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion onClick={() => toggleFAQ(index)}>
              {faq.question}
              <ToggleIcon isOpen={openFAQ === index}>{openFAQ === index ? '−' : '+'}</ToggleIcon>
            </FAQQuestion>
            {openFAQ === index && <FAQAnswer>{faq.answer}</FAQAnswer>}
          </FAQItem>
        ))}
      </FAQSection>

     
    </Home>
  );
};

export default CustomWebDesign;